'use client';

import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import type { SparkEngine, SparkJob } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';
import { dayjs } from '@/utils/dayjs';

import { SparkJobExplainSection } from './sparkJobExplainSection';
import { SparkJobStatusCell } from './sparkJobStatusCell';
import { getSparkUIUrl } from './sparkJobsTableColumns';

interface SparkJobPanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const SparkJobPanel = ({
  id,
  workspaceId,
  onClose,
}: SparkJobPanelProps) => {
  const dal = useDAL();
  const { currentProjectId } = useUser();

  const { data: jobsData, isLoading: isJobsLoading } =
    dal.lakehouse.useListSparkJobs(workspaceId);

  const { data: enginesData } = dal.lakehouse.useListSparkEngines(workspaceId);

  const job = jobsData?.sparkJobs?.find((j: SparkJob) => j.id === id);

  const engineName = useMemo(() => {
    if (!job || !enginesData?.sparkEngines) return undefined;
    return enginesData.sparkEngines.find(
      (e: SparkEngine) => e.id === job.sparkEngineId
    )?.name;
  }, [job, enginesData]);

  if (isJobsLoading || !job) {
    return (
      <PanelWrapper
        header={{
          title: 'Spark Job Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const sparkUiUrl = getSparkUIUrl(currentProjectId, workspaceId, job);

  const sparkUiOptions = sparkUiUrl
    ? [
        {
          id: 'view-spark-ui',
          text: 'View Spark UI',
          icon: 'material-symbols-open-in-new-rounded',
          menuItemProps: {
            onClick: () =>
              window.open(sparkUiUrl, '_blank', 'noopener,noreferrer'),
          },
        },
      ]
    : [];

  return (
    <PanelWrapper
      header={{
        title: 'Spark Job Details',
        onClose,
        ...(sparkUiOptions.length > 0 && {
          actions: <OptionMenu options={sparkUiOptions} />,
        }),
      }}
    >
      <Stack gap='24px' padding='24px 40px'>
        {/* Job Details */}
        <Stack gap='12px'>
          <DetailRow label='ID' value={job.id} />
          <DetailRow label='Name' value={job.name} />
          <DetailRow
            label='Status'
            value={<SparkJobStatusCell status={job.status} />}
          />
          <DetailRow
            label='Spark Engine'
            value={engineName || job.sparkEngineId}
          />
          <DetailRow
            label='Created'
            value={dayjs.utc(job.createdAt).format('MMM D, YYYY HH:mm:ss')}
          />
          {job.jobTimeout !== null && (
            <DetailRow label='Job Timeout' value={`${job.jobTimeout}s`} />
          )}
        </Stack>

        {/* Spark Submit Params */}
        {job.sparkSubmitParams && (
          <>
            <Divider />
            <Stack gap='12px'>
              <Typography variant='subtitle2' color='text.secondary'>
                Spark Submit Parameters
              </Typography>
              <DetailRow
                label='Entry Point'
                value={
                  <Typography
                    variant='body2'
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      wordBreak: 'break-all',
                      flex: 1,
                    }}
                  >
                    {job.sparkSubmitParams.entryPoint}
                  </Typography>
                }
              />
              {job.sparkSubmitParams.entryPointArguments &&
                job.sparkSubmitParams.entryPointArguments.length > 0 && (
                  <DetailRow
                    label='Arguments'
                    value={
                      <Typography
                        variant='body2'
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          flex: 1,
                        }}
                      >
                        {job.sparkSubmitParams.entryPointArguments.join('\n')}
                      </Typography>
                    }
                  />
                )}
              {job.sparkSubmitParams.sparkSubmitParameters && (
                <DetailRow
                  label='Parameters'
                  value={
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        flex: 1,
                      }}
                    >
                      {job.sparkSubmitParams.sparkSubmitParameters}
                    </Typography>
                  }
                />
              )}
            </Stack>
          </>
        )}

        {/* Retry Policy */}
        {job.retryPolicy && (
          <>
            <Divider />
            <Stack gap='12px'>
              <Typography variant='subtitle2' color='text.secondary'>
                Retry Policy
              </Typography>
              {job.retryPolicy.maxAttempts !== null && (
                <DetailRow
                  label='Max Attempts'
                  value={String(job.retryPolicy.maxAttempts)}
                />
              )}
              {job.retryPolicy.maxFailedAttemptsPerHour !== null && (
                <DetailRow
                  label='Max Failed/hr'
                  value={String(job.retryPolicy.maxFailedAttemptsPerHour)}
                />
              )}
            </Stack>
          </>
        )}

        {/* AI Explanation */}
        {['SUCCESS', 'FAILED', 'CANCELLED'].includes(job.status) &&
          !!job.sparkApplicationId && (
            <>
              <Divider />
              <SparkJobExplainSection
                sparkJobId={id}
                workspaceId={workspaceId}
                isTerminal={true}
                hasSparkApplicationId={true}
              />
            </>
          )}
      </Stack>
    </PanelWrapper>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '100px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant='body2' sx={{ wordBreak: 'break-word', flex: 1 }}>
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
